// src/components/Scoreboard.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Persistent strip across top of M6 (Your Plan) and M7 (Close) screens.
// Updated for Brief 6 — shows plan-era fields instead of lever-era fields.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { fvLump, fvContributions, mortgagePayoffMonths } from '../utils/planCalculations';
import { effectiveCTRate } from '../taxConfig';

interface Props {
  data: ClientData;
}

const fmt = (n: number | null | undefined) => n == null ? '—' : formatCurrency(n);

export default function Scoreboard({ data }: Props) {
  // Freedom Age
  const freedomAge = data.targetFreedomAge || data.voyantDoNothing.retirementAge || 0;

  // Years to freedom
  const yearsToFreedom = Math.max(0, freedomAge - data.clientAge);

  // Pot at freedom (pension + ISA projected)
  const pensionGrowth = (data.assumptionPensionGrowth || 0.08) * 100;
  const isaGrowth = (data.assumptionIsaGrowth || 0.08) * 100;
  const pensionMonthly = (data.pensionCompanyFunded || 0) + (data.planPensionFromDividendReduction || 0);
  const pensionAtFreedom = fvLump(data.pensionBalance || 0, pensionGrowth, yearsToFreedom)
    + fvContributions(pensionMonthly, pensionGrowth, yearsToFreedom);
  const isaAtFreedom = fvLump(data.investmentsBalance || 0, isaGrowth, yearsToFreedom)
    + fvContributions(data.planIsaMonthlyContribution || 0, isaGrowth, yearsToFreedom);
  const potAtFreedom = pensionAtFreedom + isaAtFreedom;

  // Tax saved/yr from dividend reduction. CT relief on employer pension uses
  // the client's own effective CT rate (19-25%) rather than a flat 19%.
  const ctRate = effectiveCTRate(data.estimatedCompanyProfit || 0);
  const taxSavedYr = data.planDividendReductionCommitted && data.planDividendReduction > 0
    ? data.planDividendReduction * 12 * 0.3375 + (data.planPensionFromDividendReduction || 0) * 12 * ctRate
    : 0;

  // Monthly committed
  const monthlyCommitted = (data.planEfMonthlyTopUp || 0)
    + (data.planDebtExtraMonthly || 0)
    + (data.planMortgageOverpayment || 0)
    + (data.planIsaMonthlyContribution || 0)
    + (data.planPensionFromDividendReduction || 0)
    + (data.monthlyHMRC || 0)
    + (data.sinkingFundsTotal || 0);

  // Net worth at freedom (simplified)
  const propGrowth = (data.assumptionPropertyGrowth || 0.01) * 100;
  const cashGrowth = (data.assumptionCashRate || 0.01) * 100;
  const propEquity = fvLump(data.propertyValue || 0, propGrowth, yearsToFreedom);
  const totalSavings = (data.savingsAccounts || []).reduce((s, a) => s + (a.balance || 0), 0)
    + (data.efAccount1Balance || 0) + (data.efAccount2Balance || 0);
  const cashAtFreedom = fvLump(totalSavings, cashGrowth, yearsToFreedom);
  const netWorth = propEquity + potAtFreedom + cashAtFreedom;

  // Mortgage free?
  let mortgageFreeLabel = '—';
  if (data.mortgageBalance > 0) {
    const termMonths = (data.mortgageTermYears || 25) * 12;
    const payoffMo = mortgagePayoffMonths(
      data.mortgageBalance, data.mortgageRate, termMonths,
      data.planMortgageOverpayment || 0, data.planMortgageAnnualLumpSum || 0
    );
    const payoffAge = data.clientAge + Math.ceil(payoffMo / 12);
    mortgageFreeLabel = payoffAge <= freedomAge ? `✓ Age ${payoffAge}` : `❌ Age ${payoffAge}`;
  } else {
    mortgageFreeLabel = '✓ No mortgage';
  }

  const hasPlan = data.planBuilt || monthlyCommitted > 0;

  const metrics = [
    { label: 'Freedom Age', value: freedomAge > 0 ? `${freedomAge}` : '—', sub: 'Target' },
    { label: 'Pot at Freedom', value: hasPlan ? fmt(potAtFreedom) : '—', sub: 'Pension + ISA' },
    { label: 'Tax Saved/Yr', value: taxSavedYr > 0 ? fmt(taxSavedYr) : '—', sub: 'From div reduction' },
    { label: 'Monthly', value: monthlyCommitted > 0 ? fmt(monthlyCommitted) : '—', sub: 'Committed', highlight: true },
    { label: 'Net Worth', value: hasPlan ? fmt(netWorth) : '—', sub: 'At freedom' },
    { label: 'Mortgage Free?', value: mortgageFreeLabel, sub: '' },
  ];

  return (
    <div className="bg-ma-navy text-white px-4 py-3 flex items-center justify-between gap-2">
      {metrics.map((m) => (
        <div key={m.label} className={`flex-1 text-center px-2 py-1 rounded-lg ${m.highlight ? 'bg-white/10' : ''}`}>
          <div className="text-white/50 text-xs font-medium uppercase tracking-wider">{m.label}</div>
          <div className={`text-lg font-bold mt-0.5 ${m.highlight ? 'text-ma-gold' : ''}`}>{m.value}</div>
          {m.sub && <div className="text-white/30 text-xs">{m.sub}</div>}
        </div>
      ))}
    </div>
  );
}
